import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Eye, Ear, Hand, Volume2, Coffee } from "lucide-react";

interface GroundingExerciseProps {
  onClose: () => void;
}

type Step = {
  title: string;
  prompt: string;
  count: number;
  icon: any;
  color: string;
};

const steps: Step[] = [
  {
    title: "5 Things You Can See",
    prompt: "Look around and name 5 things you can see right now",
    count: 5,
    icon: Eye,
    color: "from-blue-500 to-cyan-500",
  },
  {
    title: "4 Things You Can Touch",
    prompt: "Notice 4 things you can physically feel or touch",
    count: 4,
    icon: Hand,
    color: "from-purple-500 to-pink-500",
  },
  {
    title: "3 Things You Can Hear",
    prompt: "Listen carefully and identify 3 sounds around you",
    count: 3,
    icon: Ear,
    color: "from-green-500 to-teal-500",
  },
  {
    title: "2 Things You Can Smell",
    prompt: "Notice 2 scents or smells in your environment",
    count: 2,
    icon: Coffee,
    color: "from-orange-500 to-amber-500",
  },
  {
    title: "1 Thing You Can Taste",
    prompt: "Focus on 1 taste in your mouth right now",
    count: 1,
    icon: Volume2,
    color: "from-red-500 to-rose-500",
  },
];

export const GroundingExercise = ({ onClose }: GroundingExerciseProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [itemsIdentified, setItemsIdentified] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;
  const progress = ((currentStep * 100) + (itemsIdentified / currentStepData.count * 100)) / steps.length;

  const handleItemClick = () => {
    if (itemsIdentified + 1 >= currentStepData.count) {
      // Move to next step
      if (currentStep + 1 >= steps.length) {
        setIsComplete(true);
      } else {
        setCurrentStep(currentStep + 1);
        setItemsIdentified(0);
      }
    } else {
      setItemsIdentified(itemsIdentified + 1);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setItemsIdentified(0);
    setIsComplete(false);
  };

  if (isComplete) {
    return (
      <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full p-12 text-center relative">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4"
          >
            <X className="h-5 w-5" />
          </Button>

          <div className="mb-8">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 flex items-center justify-center">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold mb-4 text-foreground">You're Grounded!</h2>
            <p className="text-lg text-muted-foreground mb-2">
              Great work bringing yourself back to the present moment.
            </p>
            <p className="text-muted-foreground">
              Notice how your body and mind feel more calm and centered.
            </p>
          </div>

          <div className="flex gap-3 justify-center">
            <Button onClick={reset}>
              Start Again
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back to Toolkit
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <Button
          variant="ghost"
          onClick={onClose}
          className="mb-6 gap-2"
        >
          <X className="h-4 w-4" />
          Exit Exercise
        </Button>

        <Card className="p-8 md:p-12">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>Step {currentStep + 1} of {steps.length}</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Main Circle Display */}
          <div className="flex justify-center mb-8">
            <div className="relative w-80 h-80 flex items-center justify-center">
              {/* Animated rings */}
              {[...Array(currentStepData.count)].map((_, i) => (
                <div
                  key={i}
                  className={`absolute rounded-full border-4 transition-all duration-500 ${
                    i < itemsIdentified 
                      ? "border-primary scale-110 opacity-100" 
                      : "border-muted opacity-30"
                  }`}
                  style={{
                    width: `${100 - (i * 15)}%`,
                    height: `${100 - (i * 15)}%`,
                  }}
                />
              ))}
              
              {/* Center content */}
              <div className={`absolute rounded-full bg-gradient-to-r ${currentStepData.color} w-40 h-40 flex flex-col items-center justify-center text-white shadow-2xl transition-all duration-500`}>
                <Icon className="h-12 w-12 mb-2" />
                <div className="text-4xl font-bold">
                  {itemsIdentified}/{currentStepData.count}
                </div>
              </div>
            </div>
          </div>

          {/* Current Step Info */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-3 text-foreground">
              {currentStepData.title}
            </h2>
            <p className="text-lg text-muted-foreground">
              {currentStepData.prompt}
            </p>
          </div>

          {/* Action Button */}
          <div className="flex justify-center gap-3">
            <Button 
              size="lg" 
              onClick={handleItemClick}
              className="shadow-medium"
            >
              Identified {itemsIdentified + 1 > currentStepData.count ? currentStepData.count : itemsIdentified + 1}
            </Button>
            <Button 
              variant="outline"
              onClick={reset}
            >
              Reset
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};
